import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isBlocked: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null, isAdmin: false, isBlocked: false, loading: true });

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let authClient: any = null;

    const getSupabase = async () => {
      if (authClient) return authClient;
      const module = await import('../lib/supabase');
      authClient = module.supabase;
      return authClient;
    };

    const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T | null> => {
      return await Promise.race([
        promise,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
      ]);
    };

    const syncAdminStatus = async (currentUser: User | null) => {
      if (!currentUser) {
        setIsAdmin(false);
        setIsBlocked(false);
        return;
      }

      // Block enforcement: blocked users are not allowed to stay signed in.
      try {
        const supabase = await getSupabase();
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_blocked')
          .eq('id', currentUser.id)
          .maybeSingle();
        if (!profileError && profileData?.is_blocked) {
          setIsBlocked(true);
          setIsAdmin(false);
          await supabase.auth.signOut();
          setUser(null);
          if (typeof window !== 'undefined') {
            window.alert('Your account has been blocked. Please contact support.');
          }
          return;
        }
        setIsBlocked(false);
      } catch {
        setIsBlocked(false);
      }

      // Hardcoded Admin Check for Project Owner (Immediate fix)
      if (currentUser.email === 'mdhojyfa001@gmail.com') {
        setIsAdmin(true);
        return;
      }

      try {
        const supabase = await getSupabase();
        // Try to fetch specifically for this user
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('user_id')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (adminError) {
          console.error('Supabase Admin Check Error:', adminError.message);
          // If RLS prevents reading, we'll try a fallback check or assume false
          setIsAdmin(false);
          return;
        }

        if (adminData) {
          setIsAdmin(true);
          return;
        }

        // Fallback: Check if ANY admin exists. If table is empty, first user gets admin
        const { data: anyAdmin, error: anyAdminError } = await supabase.from('admins').select('user_id').limit(1);
        if (!anyAdminError && anyAdmin && anyAdmin.length === 0) {
          console.log("No admins found, attempting to bootstrap first admin:", currentUser.email);
          const { error: insError } = await supabase.from('admins').insert({
            user_id: currentUser.id,
            email: currentUser.email,
          });
          setIsAdmin(!insError);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error('SyncAdminStatus Exception:', e);
        setIsAdmin(false);
      }
    };

    // Check active sessions and sets up the user
    const setupAuth = async () => {
      // Add a safety timeout for auth setup
      const authTimeout = setTimeout(() => {
        if (loading) {
          console.warn("Auth setup timed out, setting loading to false anyway");
          setLoading(false);
        }
      }, 10000);

      try {
        const supabase = await getSupabase();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        // Do not block initial UI on admin-role lookup
        withTimeout(syncAdminStatus(currentUser), 5000).catch(() => null);
      } catch (err) {
        console.error("Auth Setup Error:", err);
      } finally {
        clearTimeout(authTimeout);
        setLoading(false);
      }
    };

    setupAuth();

    // Listen for auth changes
    let subscription: { unsubscribe: () => void } | null = null;
    getSupabase().then((supabase) => {
      const result = supabase.auth.onAuthStateChange(async (_event: string, session: any) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        // Keep auth reactive but non-blocking for faster paint
        withTimeout(syncAdminStatus(currentUser), 5000).catch(() => null);
        setLoading(false);
      });
      subscription = result.data.subscription;
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAdmin, isBlocked, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
